import { useCallback, useRef, useState } from 'react';

import { useSyncSmartFolder } from './mutations/useSyncSmartFolder.ts';
import { useFolderSyncActions } from './SmartFolderSyncContext.tsx';
import type { SmartFolderErrorState } from './SmartFolderErrorConstants.ts';
import { getSyncErrorState } from './SmartFolderErrorConstants.ts';

export type SmartFolderSyncStatus = 'idle' | 'syncing' | 'success' | 'error';

type UseSmartFolderSyncOptions = {
	/** Called after a successful sync so consumers can refresh their data (e.g. page tree, folder view). */
	onSyncComplete?: () => void;
};

type UseSmartFolderSyncResult = {
	/** Trigger a sync for the given folder. Safe to call multiple times – concurrent calls for the same folder are de-duplicated. */
	triggerSync: (folderId: string) => void;
	/** Current sync status. */
	status: SmartFolderSyncStatus;
	/**
	 * A UI-intent token set when a sync fails with a known, actionable GQL error
	 * (e.g. owner deactivated, access lost, folder deleted). Maps directly to a
	 * SmartFolderError screen variant. Null for transient/network errors — callers
	 * should fall back to inline error handling in those cases.
	 */
	syncErrorState: SmartFolderErrorState | null;
};

/**
 * Hook that triggers a smart-folder sync and exposes the current sync status.
 *
 * Designed to be called when a user opens a folder view or navigates to
 * content within a smart folder. Duplicate concurrent calls for the same
 * folder are de-duplicated. The syncing folder ID is published via
 * SmartFolderSyncContext so other components (e.g. the page tree) can show a
 * loading indicator.
 */
export const useSmartFolderSync = (
	options?: UseSmartFolderSyncOptions,
): UseSmartFolderSyncResult => {
	const { onSyncComplete } = options ?? {};
	const { syncSmartFolder, loading } = useSyncSmartFolder();
	const { addSyncingFolder, removeSyncingFolder } = useFolderSyncActions();
	const syncingFolderRef = useRef<string | null>(null);
	const statusRef = useRef<SmartFolderSyncStatus>('idle');
	const [syncErrorState, setSyncErrorState] = useState<SmartFolderErrorState | null>(null);
	// Derive a simple status from the mutation state + our local tracking
	const status: SmartFolderSyncStatus = loading ? 'syncing' : statusRef.current;

	const triggerSync = useCallback(
		(folderId: string) => {
			// De-duplicate: don't fire another request if we're already syncing this folder
			if (syncingFolderRef.current === folderId) {
				return;
			}

			syncingFolderRef.current = folderId;
			statusRef.current = 'syncing';
			setSyncErrorState(null);
			addSyncingFolder(folderId);

			syncSmartFolder({ folderId })
				.then((result) => {
					const success = result?.data?.confluence_syncSmartFolder?.success ?? false;
					statusRef.current = success ? 'success' : 'error';

					if (success) {
						onSyncComplete?.();
					} else {
						// TODO(CCINT-8155): Once the BE schema is deployed and `extensions` is
						// available in the generated types, replace the `as any` cast with the
						// proper typed access:
						// result.data?.confluence_syncSmartFolder?.errors?.[0]?.extensions?.[0]?.errorType
						const payload = result.data?.confluence_syncSmartFolder;
						const rawErrorType = (payload?.errors?.[0] as any)?.extensions?.[0]?.errorType; // NOSONAR - `as any` is intentional: extensions is not yet in the generated GQL types (see TODO(CCINT-8155) above)

						// Map the raw GQL errorType to a UI-intent token. Only known, actionable
						// errors produce a non-null value (e.g. owner deactivated, access lost,
						// folder deleted). Transient/network errors return null so callers fall
						// back to inline error handling.
						const structuredError =
							typeof rawErrorType === 'string' ? getSyncErrorState(rawErrorType) : null;
						setSyncErrorState(structuredError);
					}
				})
				.catch(() => {
					statusRef.current = 'error';
				})
				.finally(() => {
					const finishedId = syncingFolderRef.current;
					syncingFolderRef.current = null;
					if (finishedId) {
						removeSyncingFolder(finishedId);
					}
					statusRef.current = 'idle';
				});
		},
		[syncSmartFolder, onSyncComplete, addSyncingFolder, removeSyncingFolder],
	);

	return { triggerSync, status, syncErrorState };
};
